import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { aiPowerAr } from './ai-power/ar';
import { aiPowerEn } from './ai-power/en';
import { aiPowerHi } from './ai-power/hi';
import { aiPowerUr } from './ai-power/ur';
import { ar } from './ar';
import { billingAr } from './billing/ar';
import { billingEn } from './billing/en';
import { billingHi } from './billing/hi';
import { billingUr } from './billing/ur';
import { en } from './en';
import { hi } from './hi';
import { inboxAlertsAr } from './inbox-alerts/ar';
import { inboxAlertsEn } from './inbox-alerts/en';
import { inboxAlertsHi } from './inbox-alerts/hi';
import { inboxAlertsUr } from './inbox-alerts/ur';
import { migrationAr } from './migration/ar';
import { migrationEn } from './migration/en';
import { migrationHi } from './migration/hi';
import { migrationUr } from './migration/ur';
import { paletteAr } from './palette/ar';
import { paletteEn } from './palette/en';
import { paletteHi } from './palette/hi';
import { paletteUr } from './palette/ur';
import { platformCopy } from './platform';
import { setupAr } from './setup/ar';
import { setupEn } from './setup/en';
import { setupHi } from './setup/hi';
import { setupUr } from './setup/ur';
import { ur } from './ur';

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'translation';
    resources: {
      translation: typeof en & {
        migration: typeof migrationEn;
        billing: typeof billingEn;
        setup: typeof setupEn;
        inboxAlerts: typeof inboxAlertsEn;
        aiPower: typeof aiPowerEn;
        palette: typeof paletteEn;
        agentic: typeof platformCopy.en;
      };
    };
  }
}

export const LOCALES = ['en', 'ar', 'hi', 'ur'] as const;
export type Locale = (typeof LOCALES)[number];
export const RTL_LOCALES = new Set<Locale>(['ar', 'ur']);
/** Each language named in itself, so people can find their own. */
export const LANGUAGE_NAMES: Record<Locale, string> = {
  en: 'English',
  ar: 'العربية',
  hi: 'हिन्दी',
  ur: 'اردو',
};

const STORAGE_KEY = 'hamnivo.studio.locale';

export const isLocale = (value: unknown): value is Locale =>
  typeof value === 'string' && (LOCALES as readonly string[]).includes(value);

export const directionOf = (locale: Locale) => (RTL_LOCALES.has(locale) ? 'rtl' : 'ltr');

function stored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** ?lang= (for shared links), then the saved choice, then the browser's language. */
export function initialLocale(): Locale {
  const fromQuery = new URLSearchParams(window.location.search).get('lang');
  if (isLocale(fromQuery)) return fromQuery;
  const saved = stored();
  if (isLocale(saved)) return saved;
  const browser = navigator.language?.toLowerCase().split('-')[0];
  return isLocale(browser) ? browser : 'en';
}

export async function initI18n(locale: Locale) {
  await i18next.use(initReactI18next).init({
    resources: {
      en: {
        translation: {
          ...en,
          agentic: platformCopy.en,
          migration: migrationEn,
          billing: billingEn,
          setup: setupEn,
          inboxAlerts: inboxAlertsEn,
          aiPower: aiPowerEn,
          palette: paletteEn,
        },
      },
      ar: {
        translation: {
          ...ar,
          agentic: platformCopy.ar,
          migration: migrationAr,
          billing: billingAr,
          setup: setupAr,
          inboxAlerts: inboxAlertsAr,
          aiPower: aiPowerAr,
          palette: paletteAr,
        },
      },
      hi: {
        translation: {
          ...hi,
          agentic: platformCopy.hi,
          migration: migrationHi,
          billing: billingHi,
          setup: setupHi,
          inboxAlerts: inboxAlertsHi,
          aiPower: aiPowerHi,
          palette: paletteHi,
        },
      },
      ur: {
        translation: {
          ...ur,
          agentic: platformCopy.ur,
          migration: migrationUr,
          billing: billingUr,
          setup: setupUr,
          inboxAlerts: inboxAlertsUr,
          aiPower: aiPowerUr,
          palette: paletteUr,
        },
      },
    },
    lng: locale,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  return i18next;
}

/** Sets the document language and direction (right to left for Arabic and Urdu). */
export function applyLocale(locale: Locale) {
  document.documentElement.lang = locale;
  document.documentElement.dir = directionOf(locale);
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // The choice then lasts for this page only.
  }
}

export async function changeLocale(locale: Locale) {
  applyLocale(locale);
  await i18next.changeLanguage(locale);
}
